import { Cursors, DEFAULT_SNAP_ANGLE, DEFAULT_SNAP_SIZE, ITEMBOUNDSCHANGE, ITEMROTATE, MIN_SNAP_ANGLE, MIN_SNAP_SIZE, TRANSPARENT } from './../../constants';
import { Shape } from './../../dom/Shape';
import { Point, Rect } from './../../math';
import { Rectangle } from './../../svg';
import { defined, findAngle, isDefined } from './../../utils';
import { deepExtend } from './../../utils/deep-extend';
import { canDrag } from '../tools/canDrag';
import { TransformUnit } from '../TransformUnit';
import { AdornerBase } from './AdornerBase';
import { RotateUnit } from '../RotateUnit';
function hitToOppositeSide(hit, bounds) {
    let result;
    if (hit.x === -1 && hit.y === -1) {
        result = bounds.bottomRight();
    }
    else if (hit.x === 1 && hit.y === 1) {
        result = bounds.topLeft();
    }
    else if (hit.x === -1 && hit.y === 1) {
        result = bounds.topRight();
    }
    else if (hit.x === 1 && hit.y === -1) {
        result = bounds.bottomLeft();
    }
    else if (hit.x === 0 && hit.y === -1) {
        result = bounds.bottom();
    }
    else if (hit.x === 0 && hit.y === 1) {
        result = bounds.top();
    }
    else if (hit.x === 1 && hit.y === 0) {
        result = bounds.left();
    }
    else if (hit.x === -1 && hit.y === 0) {
        result = bounds.right();
    }
    return result;
}
const defaultOptions = {
    handles: {
        fill: {
            color: '#fff'
        },
        stroke: {
            color: '#282828'
        },
        height: 7,
        width: 7,
        hover: {
            fill: {
                color: '#282828'
            },
            stroke: {
                color: '#282828'
            }
        }
    },
    selectable: {
        stroke: {
            color: '#778899',
            width: 1,
            dashType: 'solid'
        },
        fill: {
            color: TRANSPARENT
        },
        offset: 3
    },
    offset: 1
};
export class ResizingAdorner extends AdornerBase {
    constructor(diagram, options) {
        options = deepExtend({}, defaultOptions, options);
        // Apply offset from editable.resize.offset if configured
        const editable = diagram.options.editable;
        if (editable && typeof editable !== 'boolean' && editable.resize && typeof editable.resize !== 'boolean') {
            if (editable.resize.offset !== undefined) {
                options.offset = editable.resize.offset;
            }
        }
        // Apply selection rectangle offset from selectable.offset if configured
        // Default is 10 (same as handle offset by default)
        const selectable = diagram.options.selectable;
        if (selectable && typeof selectable !== 'boolean' && selectable.offset !== undefined) {
            options.selectionRectOffset = selectable.offset;
        }
        else {
            options.selectionRectOffset = defaultOptions.selectable.offset;
        }
        super(diagram, options);
        this._manipulating = false;
        this.map = [];
        this.shapes = [];
        this.shapeStates = [];
        this._initSelection();
        this._createHandles();
        this.redraw();
        this.diagram.bind('select', () => {
            this._initialize();
        });
        this._refreshHandler = () => {
            if (!this._internalChange) {
                this.refreshBounds();
                this.refresh();
            }
        };
        this._rotatedHandler = () => {
            if (this.shapes.length === 1) {
                this._angle = this.shapes[0].rotate().angle;
            }
            this._refreshHandler();
        };
        this.diagram.bind(ITEMBOUNDSCHANGE, this._refreshHandler).bind(ITEMROTATE, this._rotatedHandler);
        this.refreshBounds();
        this.refresh();
    }
    _initSelection() {
        const diagram = this.diagram;
        const editable = diagram.options.editable;
        // Check for selectable in editable.resize.selectable first, then fall back to diagram.options.selectable
        let selectable = diagram.options.selectable;
        if (editable && typeof editable !== 'boolean' && editable.resize && typeof editable.resize !== 'boolean' && editable.resize.selectable) {
            selectable = editable.resize.selectable;
        }
        const options = deepExtend({}, this.options.selectable, selectable);
        this.rect = new Rectangle(options);
        this.visual.append(this.rect);
    }
    _resizable() {
        return this.options.editable && this.options.editable.resize !== false;
    }
    _handleOptions() {
        const resizeHandles = (this.options.editable.resize || {}).handles;
        // Merge theme/user handles with defaults to ensure width/height are present
        return resizeHandles ? deepExtend({}, this.options.handles, resizeHandles) : this.options.handles;
    }
    _createHandles() {
        let handles, item, y, x;
        if (this._resizable()) {
            handles = this._handleOptions();
            for (x = -1; x <= 1; x++) {
                for (y = -1; y <= 1; y++) {
                    if ((x !== 0) || (y !== 0)) { // (0, 0) element, (-1, -1) top-left, (+1, +1) bottom-right
                        item = new Rectangle(handles);
                        const isMiddleHandle = x === 0 || y === 0;
                        item.drawingElement._hover = this._hover.bind(this, isMiddleHandle);
                        this.map.push({ x: x, y: y, visual: item });
                        this.visual.append(item);
                    }
                }
            }
        }
    }
    bounds(value) {
        if (value) {
            this._innerBounds = value.clone();
            this._bounds = this.diagram.modelToLayer(value).inflate(this.options.offset, this.options.offset);
        }
        else {
            return this._bounds;
        }
    }
    _hitTest(p) {
        const handlesCount = this.map.length;
        let tp = this.diagram.modelToLayer(p), i, hit, handleBounds, handle;
        if (this._angle) {
            tp = tp.clone().rotate(this._angle, this._bounds.center());
        }
        if (this._resizable()) {
            // Calculate visual container position (innerBounds - selectionRectOffset)
            const innerLayerBounds = this.diagram.modelToLayer(this._innerBounds);
            const zoom = this.diagram.zoom();
            const selectionRectOffsetLayer = this.options.selectionRectOffset * zoom;
            const containerX = innerLayerBounds.x - selectionRectOffsetLayer;
            const containerY = innerLayerBounds.y - selectionRectOffsetLayer;
            // Test corner handles first (they have priority over middle handles)
            for (i = 0; i < handlesCount; i++) {
                handle = this.map[i];
                // Corner handle: both x and y are != 0
                if (handle.x !== 0 && handle.y !== 0) {
                    hit = new Point(handle.x, handle.y);
                    handleBounds = this._getHandleBounds(hit); // local coordinates
                    handleBounds.offset(containerX, containerY);
                    if (handleBounds.contains(tp)) {
                        return hit;
                    }
                }
            }
            // Then test middle handles
            for (i = 0; i < handlesCount; i++) {
                handle = this.map[i];
                // Middle handle: either x or y is 0 (but not both)
                if (handle.x === 0 || handle.y === 0) {
                    hit = new Point(handle.x, handle.y);
                    handleBounds = this._getHandleBounds(hit); // local coordinates
                    handleBounds.offset(containerX, containerY);
                    if (handleBounds.contains(tp)) {
                        return hit;
                    }
                }
            }
        }
        if (this._bounds.contains(tp)) {
            return new Point(0, 0);
        }
    }
    _getHandleBounds(p) {
        if (this._resizable()) {
            const handles = this._handleOptions(), w = handles.width, h = handles.height;
            // Get the inner bounds in layer coordinates (with zoom applied)
            const innerLayerBounds = this.diagram.modelToLayer(this._innerBounds);
            const zoom = this.diagram.zoom();
            const offsetLayer = this.options.offset * zoom;
            const selectionRectOffsetLayer = this.options.selectionRectOffset * zoom;
            // Visual container is positioned at innerBounds - selectionRectOffset
            // Handles should be at innerBounds - offset
            // So offset from container is: selectionRectOffset - offset
            const handleOffset = selectionRectOffsetLayer - offsetLayer;
            // innerLayerBounds includes +1 which gets scaled, so we subtract zoom for width/height calculations
            const innerWidth = innerLayerBounds.width - zoom;
            const innerHeight = innerLayerBounds.height - zoom;
            const middleHandleThicknessWidth = w * zoom, middleHandleThicknessHeight = h * zoom;
            let r;
            // Corner handles - use original width/height
            if (p.x !== 0 && p.y !== 0) {
                r = new Rect(0, 0, w, h);
                if (p.x < 0) {
                    r.x = handleOffset - w / 2;
                }
                else if (p.x > 0) {
                    r.x = innerWidth + 2 * offsetLayer + handleOffset - w / 2;
                }
                if (p.y < 0) {
                    r.y = handleOffset - h / 2;
                }
                else if (p.y > 0) {
                    r.y = innerHeight + 2 * offsetLayer + handleOffset - h / 2;
                }
            }
            // Middle handles - thin lines along the entire side
            else {
                if (p.x === 0 && p.y !== 0) {
                    // Top or bottom middle - full width, thin height
                    r = new Rect(handleOffset, p.y < 0 ? handleOffset - middleHandleThicknessHeight / 2 : innerHeight + 2 * offsetLayer + handleOffset - middleHandleThicknessHeight / 2, innerWidth + 2 * offsetLayer, middleHandleThicknessHeight);
                }
                else if (p.y === 0 && p.x !== 0) {
                    // Left or right middle - thin width, full height
                    r = new Rect(p.x < 0 ? handleOffset - middleHandleThicknessWidth / 2 : innerWidth + 2 * offsetLayer + handleOffset - middleHandleThicknessWidth / 2, handleOffset, middleHandleThicknessWidth, innerHeight + 2 * offsetLayer);
                }
            }
            return r;
        }
    }
    _getCursor(point) {
        let hit = this._hitTest(point);
        if (hit && (hit.x >= -1) && (hit.x <= 1) && (hit.y >= -1) && (hit.y <= 1) && this._resizable()) {
            const angle = this._angle;
            if (angle) {
                hit.rotate(angle, new Point(0, 0));
                hit = new Point(Math.round(hit.x), Math.round(hit.y));
            }
            if (hit.x === -1 && hit.y === -1) {
                return 'nw-resize';
            }
            if (hit.x === 1 && hit.y === 1) {
                return 'se-resize';
            }
            if (hit.x === -1 && hit.y === 1) {
                return 'sw-resize';
            }
            if (hit.x === 1 && hit.y === -1) {
                return 'ne-resize';
            }
            if (hit.x === 0 && hit.y === -1) {
                return 'n-resize';
            }
            if (hit.x === 0 && hit.y === 1) {
                return 's-resize';
            }
            if (hit.x === 1 && hit.y === 0) {
                return 'e-resize';
            }
            if (hit.x === -1 && hit.y === 0) {
                return 'w-resize';
            }
        }
        return this._manipulating ? Cursors.move : Cursors.select;
    }
    _initialize() {
        let i, item;
        const items = this.diagram.select();
        this.shapes = [];
        for (i = 0; i < items.length; i++) {
            item = items[i];
            if (item instanceof Shape) {
                this.shapes.push(item);
                item._rotationOffset = new Point();
            }
        }
        this._angle = this.shapes.length === 1 ? this.shapes[0].rotate().angle : 0;
        this._startAngle = this._angle;
        this._rotates();
        this._positions();
        this.refreshBounds();
        this.refresh();
        this.redraw();
    }
    _rotates() {
        let i, shape;
        this.initialRotates = [];
        for (i = 0; i < this.shapes.length; i++) {
            shape = this.shapes[i];
            this.initialRotates.push(shape.rotate().angle);
        }
    }
    _positions() {
        let i, shape;
        this.initialStates = [];
        for (i = 0; i < this.shapes.length; i++) {
            shape = this.shapes[i];
            this.initialStates.push(shape.bounds());
        }
    }
    _hover(isMiddleHandle, value, element) {
        if (this._resizable()) {
            // Middle handles should always remain transparent
            if (isMiddleHandle) {
                element.fill('transparent', 0);
                element.stroke('transparent', 0, 0);
                return;
            }
            // Corner handles get normal hover styling
            const handleOptions = this._handleOptions(), hover = handleOptions.hover;
            let stroke = handleOptions.stroke, fill = handleOptions.fill;
            if (value && isDefined(hover.stroke)) {
                stroke = deepExtend({}, stroke, hover.stroke);
            }
            if (value && isDefined(hover.fill)) {
                fill = hover.fill;
            }
            element.stroke(stroke.color, stroke.width, stroke.opacity);
            element.fill(fill.color, fill.opacity);
        }
    }
    start(p) {
        this._sp = p;
        this._cp = p;
        this._lp = p;
        this._manipulating = true;
        this._internalChange = true;
        this.shapeStates = [];
        for (let i = 0; i < this.shapes.length; i++) {
            const shape = this.shapes[i];
            this.shapeStates.push(shape.bounds());
        }
    }
    redraw() {
        let i, handle;
        const visibleHandles = this._resizable();
        for (i = 0; i < this.map.length; i++) {
            handle = this.map[i];
            handle.visual.visible(visibleHandles);
        }
    }
    angle(value) {
        if (defined(value)) {
            this._angle = value;
        }
        return this._angle;
    }
    rotate() {
        const center = this._innerBounds.center();
        let currentAngle = this.angle();
        this._internalChange = true;
        for (let i = 0; i < this.shapes.length; i++) {
            const shape = this.shapes[i];
            currentAngle = (currentAngle + this.initialRotates[i] - this._startAngle) % 360;
            shape.rotate(currentAngle, center);
        }
        this.refresh();
    }
    move(handle, p) {
        let delta, dragging, dtl = new Point(), dbr = new Point(), bounds, center, shape, i, angle, newBounds, changed = 0, staticPoint, scaleX, scaleY;
        if (handle.y === -2 && handle.x === -1) {
            center = this._innerBounds.center();
            this._angle = this._truncateAngle(findAngle(center, p));
            for (i = 0; i < this.shapes.length; i++) {
                shape = this.shapes[i];
                angle = (this._angle + this.initialRotates[i] - this._startAngle) % 360;
                shape.rotate(angle, center);
                if (Object.prototype.hasOwnProperty.call(shape, 'layout')) {
                    shape.layout(shape);
                }
                this._rotating = true;
            }
            this.refresh();
        }
        else {
            if (this.shouldSnap()) {
                const thr = this._truncateDistance(p.minus(this._lp));
                // threshold
                if (thr.x === 0 && thr.y === 0) {
                    this._cp = p;
                    return;
                }
                delta = thr;
                this._lp = new Point(this._lp.x + thr.x, this._lp.y + thr.y);
            }
            else {
                delta = p.minus(this._cp);
            }
            if (this.isDragHandle(handle)) {
                dbr = dtl = delta; // dragging
                dragging = true;
            }
            else {
                if (this._angle) { // adjust the delta so this resizers resize in the correct direction after rotation.
                    delta.rotate(this._angle, new Point(0, 0));
                }
                if (handle.x === -1) {
                    dtl.x = delta.x;
                }
                else if (handle.x === 1) {
                    dbr.x = delta.x;
                }
                if (handle.y === -1) {
                    dtl.y = delta.y;
                }
                else if (handle.y === 1) {
                    dbr.y = delta.y;
                }
            }
            if (!dragging) {
                staticPoint = hitToOppositeSide(handle, this._innerBounds);
                scaleX = (this._innerBounds.width + delta.x * handle.x) / this._innerBounds.width;
                scaleY = (this._innerBounds.height + delta.y * handle.y) / this._innerBounds.height;
            }
            for (i = 0; i < this.shapes.length; i++) {
                shape = this.shapes[i];
                bounds = shape.bounds();
                if (dragging) {
                    if (!canDrag(shape)) {
                        continue;
                    }
                    newBounds = this._displaceBounds(bounds, dtl, dbr, dragging);
                }
                else {
                    newBounds = bounds.clone();
                    newBounds.scale(scaleX, scaleY, staticPoint, this._innerBounds.center(), shape.rotate().angle);
                    const newCenter = newBounds.center(); // fixes the new rotation center.
                    newCenter.rotate(-this._angle, bounds.center());
                    newBounds = new Rect(newCenter.x - newBounds.width / 2, newCenter.y - newBounds.height / 2, newBounds.width, newBounds.height);
                }
                if (newBounds.width >= shape.options.minWidth && newBounds.height >= shape.options.minHeight) { // if we up-size very small shape
                    const oldBounds = bounds;
                    shape.bounds(newBounds);
                    if (Object.prototype.hasOwnProperty.call(shape, 'layout')) {
                        shape.layout(shape, oldBounds, newBounds);
                    }
                    if (oldBounds.width !== newBounds.width || oldBounds.height !== newBounds.height) {
                        shape.rotate(shape.rotate().angle); // forces the rotation to update it's rotation center
                    }
                    changed += 1;
                }
            }
            if (changed) {
                if (changed === i) {
                    newBounds = this._displaceBounds(this._innerBounds, dtl, dbr, dragging);
                    this.bounds(newBounds);
                }
                else {
                    this.refreshBounds();
                }
                this.refresh();
            }
            this._positions();
        }
        this._cp = p;
    }
    isDragHandle(handle) {
        return handle.x === 0 && handle.y === 0;
    }
    cancel() {
        const shapes = this.shapes;
        const states = this.shapeStates;
        for (let idx = 0; idx < shapes.length; idx++) {
            shapes[idx].bounds(states[idx]);
        }
        this.refreshBounds();
        this.refresh();
        this._manipulating = undefined;
        this._internalChange = undefined;
        this._rotating = undefined;
    }
    _truncatePositionToGuides(bounds) {
        if (this.diagram.ruler) {
            return this.diagram.ruler.truncatePositionToGuides(bounds);
        }
        return bounds;
    }
    _truncateSizeToGuides(bounds) {
        if (this.diagram.ruler) {
            return this.diagram.ruler.truncateSizeToGuides(bounds);
        }
        return bounds;
    }
    _truncateAngle(a) {
        const snap = this.snapOptions();
        const snapAngle = Math.max(snap.angle || DEFAULT_SNAP_ANGLE, MIN_SNAP_ANGLE);
        return snap ? Math.floor((a % 360) / snapAngle) * snapAngle : (a % 360);
    }
    _truncateDistance(d) {
        if (d instanceof Point) {
            return new Point(this._truncateDistance(d.x), this._truncateDistance(d.y));
        }
        else {
            const snap = this.snapOptions() || {};
            const snapSize = Math.max(snap.size || DEFAULT_SNAP_SIZE, MIN_SNAP_SIZE);
            return snap ? Math.floor(d / snapSize) * snapSize : d;
        }
    }
    snapOptions() {
        const editable = this.diagram.options.editable;
        const snap = ((editable || {}).drag || {}).snap || {};
        return snap;
    }
    shouldSnap() {
        const editable = this.diagram.options.editable;
        const drag = (editable || {}).drag;
        const snap = (drag || {}).snap;
        return editable !== false && drag !== false && snap !== false;
    }
    _displaceBounds(bounds, dtl, dbr, dragging) {
        const tl = bounds.topLeft().plus(dtl), br = bounds.bottomRight().plus(dbr);
        let newBounds = Rect.fromPoints(tl, br), newCenter;
        if (!dragging) {
            newCenter = newBounds.center();
            newCenter.rotate(-this._angle, bounds.center());
            newBounds = new Rect(newCenter.x - newBounds.width / 2, newCenter.y - newBounds.height / 2, newBounds.width, newBounds.height);
        }
        return newBounds;
    }
    stop() {
        var _a, _b;
        let unit, i, shape, bounds;
        if (this._cp !== this._sp) {
            if (this._rotating) {
                unit = new RotateUnit(this, this.shapes, this.initialRotates);
                this._rotating = false;
            }
            else if (this._diffStates()) {
                if (this.diagram.ruler) {
                    for (i = 0; i < this.shapes.length; i++) {
                        shape = this.shapes[i];
                        bounds = shape.bounds();
                        bounds = this._truncateSizeToGuides(this._truncatePositionToGuides(bounds));
                        shape.bounds(bounds);
                        this.refreshBounds();
                        this.refresh();
                    }
                }
                for (i = 0; i < this.shapes.length; i++) {
                    shape = this.shapes[i];
                    bounds = shape.bounds();
                    const strokeWidth = (((_b = (_a = shape.shapeVisual) === null || _a === void 0 ? void 0 : _a.options.stroke) === null || _b === void 0 ? void 0 : _b.width) || 0);
                    if (shape._originalWidth + strokeWidth !== bounds.width) {
                        shape._originalWidth = bounds.width - strokeWidth;
                    }
                    if (shape._originalHeight + strokeWidth !== bounds.height) {
                        shape._originalHeight = bounds.height - strokeWidth;
                    }
                    shape.updateModel();
                }
                unit = new TransformUnit(this.shapes, this.shapeStates, this);
                this.diagram._syncShapeChanges();
            }
        }
        this._manipulating = undefined;
        this._internalChange = undefined;
        this._rotating = undefined;
        return unit;
    }
    _diffStates() {
        const shapes = this.shapes;
        const states = this.shapeStates;
        for (let idx = 0; idx < shapes.length; idx++) {
            if (!shapes[idx].bounds().equals(states[idx])) {
                return true;
            }
        }
        return false;
    }
    refreshBounds() {
        const bounds = this.shapes.length === 1 ?
            this.shapes[0].bounds().clone() :
            this.diagram.boundingBox(this.shapes, true);
        this.bounds(bounds);
    }
    refresh() {
        let b, bounds;
        if (this.shapes.length > 0) {
            bounds = this.bounds();
            this.visual.visible(true);
            // Position visual container at innerBounds - selectionRectOffset (in layer coordinates)
            const innerLayerBounds = this.diagram.modelToLayer(this._innerBounds);
            const zoom = this.diagram.zoom();
            const selectionRectOffsetLayer = this.options.selectionRectOffset * zoom;
            this.visual.position(new Point(innerLayerBounds.x - selectionRectOffsetLayer, innerLayerBounds.y - selectionRectOffsetLayer));
            this.map.forEach((m) => {
                b = this._getHandleBounds(new Point(m.x, m.y));
                m.visual.position(b.topLeft());
                m.visual.redraw({ width: b.width, height: b.height });
                // Middle handles are transparent (invisible for hover too)
                const isMiddleHandle = m.x === 0 || m.y === 0;
                if (isMiddleHandle) {
                    // Make middle handles completely transparent
                    const rect = m.visual.drawingElement;
                    rect.fill('transparent', 0);
                    rect.stroke('transparent', 0, 0);
                }
            });
            const center = new Point(innerLayerBounds.width / 2 + selectionRectOffsetLayer, innerLayerBounds.height / 2 + selectionRectOffsetLayer);
            this.visual.rotate(this._angle, center);
            // Selection rect is positioned at origin (0,0) relative to visual container
            this.rect.position(new Point(0, 0));
            // Selection rect size: innerBounds + 2*selectionRectOffset
            // Note: innerLayerBounds includes +1 from Rect operations which gets scaled by zoom, so we subtract it
            this.rect.redraw({ width: innerLayerBounds.width + 2 * selectionRectOffsetLayer - zoom, height: innerLayerBounds.height + 2 * selectionRectOffsetLayer - zoom });
            if (this.rotationThumb) {
                const thumb = this.options.editable.rotate.thumb;
                this._rotationThumbBounds = new Rect(bounds.center().x, bounds.y + thumb.y, 0, 0).inflate(thumb.width);
                this.rotationThumb.redraw({ x: bounds.width / 2 - thumb.width / 2 });
            }
        }
        else {
            this.visual.visible(false);
        }
    }
}
